import el from "@cypherpotato/el";
import { Button, DangerButton, PrimaryButton } from "../../components/button";
import { Card, CardNavigation } from "../../components/card";
import { CommandBar } from "../../components/command-bar";
import { Dashboard } from "../../components/dashboard";
import { Editor, EditorDescription, EditorTitle } from "../../components/editor";
import { FormControl, Select, TextArea } from "../../components/form";
import { GatewayPickerControl } from "../../components/gateway-picker";
import { Icon } from "../../components/icon";
import { JsonControl } from "../../components/json-control";
import { Playground, PlaygroundAside, PlaygroundAsideContainer, PlaygroundContent } from "../../components/playground";
import { HorizontalSizableContainer, HorizontalSizableSection, SizableContainer, SizableSection } from "../../components/sizable-container";
import { TabControl } from "../../components/tabcontrol";
import { MutedText, Paragraph } from "../../components/text";
import { Constants } from "../../constants";
import { getSession } from "../../session";
import { displayAlert, displayFormControl } from "../../ui/alert";
import { Dropdown, DropdownItem } from "../../components/dropdown";
import { copyToClipboard } from "../../ui/clipboard";

const REALTIME_SAMPLE_RATE = 24000;
const MAX_VISIBLE_EVENTS = 200;
const DEFAULT_TOOLS = [];

export default function () {
    const connectionState = el.state.create("disconnected");
    const voiceActivity = el.state.create("idle");
    const events = el.state.create([]);
    const pendingToolCalls = el.state.create([]);
    const gatewayControl = Editor(
        {
            id: "voiceGateway",
            value: null,
            renderValue(value) {
                return Button(value || MutedText("None"));
            },
            manipulateValue(value, callback) {
                displayFormControl({
                    title: this.editorTitle,
                    message: this.editorParagraphs,
                    formControlItem: GatewayPickerControl({ value, allowNone: true }),
                    onConfirm: callback,
                    validate: [],
                    dialogSize: "large"
                });
            }
        },
        EditorDescription(
            EditorTitle("Gateway ID")
        )
    );
    const modelControl = Editor(
        {
            id: "voiceSessionModel",
            value: Constants.VoiceSessionModels[0],
            renderValue(value) {
                return Button(value);
            },
            manipulateValue(value, callback) {
                displayFormControl({
                    title: this.editorTitle,
                    message: this.editorParagraphs,
                    formControlItem: Select({
                        value,
                        items: Constants.VoiceSessionModels.map(model => ({ value: model, text: model }))
                    }),
                    onConfirm: callback,
                    validate: []
                });
            }
        },
        EditorDescription(
            EditorTitle("Model")
        )
    );
    const voiceControl = Editor(
        {
            id: "voiceSessionVoice",
            value: Constants.VoiceSessionVoices[0],
            renderValue(value) {
                return Button(value);
            },
            manipulateValue(value, callback) {
                displayFormControl({
                    title: this.editorTitle,
                    message: this.editorParagraphs,
                    formControlItem: Select({
                        value,
                        items: Constants.VoiceSessionVoices.map(voice => ({ value: voice, text: voice }))
                    }),
                    onConfirm: callback,
                    validate: []
                });
            }
        },
        EditorDescription(
            EditorTitle("Voice")
        )
    );
    const reasoningControl = Editor(
        {
            id: "voiceSessionReasoningEffort",
            value: "minimal",
            renderValue(value) {
                return Button(Constants.VoiceSessionReasoningEfforts.find(item => item.value === value)?.text || value);
            },
            manipulateValue(value, callback) {
                displayFormControl({
                    title: this.editorTitle,
                    message: this.editorParagraphs,
                    formControlItem: Select({
                        value,
                        items: Constants.VoiceSessionReasoningEfforts
                    }),
                    onConfirm: callback,
                    validate: []
                });
            }
        },
        EditorDescription(
            EditorTitle("Reasoning effort")
        )
    );
    const contextControl = TextArea(null, null, {
        id: "voiceSessionContext",
        placeholder: "Describe the assistant, the conversation context, and any constraints...",
        ariaLabel: "Voice session context"
    });
    const toolsControl = JsonControl({
        id: "voiceSessionTools",
        value: DEFAULT_TOOLS,
        ariaLabel: "Voice session tools"
    });
    let socket = null;
    let audioContext = null;
    let microphoneStream = null;
    let microphoneSource = null;
    let microphoneProcessor = null;
    let silentOutput = null;
    let playbackQueue = [];
    let scheduledPlaybacks = [];
    let playbackEndTime = 0;
    let activeResponseId = null;
    let disposed = false;

    const responsePlayback = new Map();
    const itemPlaybackMs = new Map();
    const cancelledResponseIds = new Set();
    const completedToolResponses = new Set();

    function setVoiceActivity(activity) {
        if (voiceActivity.value !== activity) {
            voiceActivity.value = activity;
        }
    }

    function appendEvent(direction, payload) {
        const event = {
            id: `${Date.now()}-${Math.random()}`,
            direction,
            time: new Date(),
            payload: payload.type === "response.output_audio.delta" && typeof payload.delta === "string"
                ? { ...payload, delta: `[${payload.delta.length} base64 characters]` }
                : payload
        };
        events.value = [...events.value.slice(-(MAX_VISIBLE_EVENTS - 1)), event];
        requestAnimationFrame(() => {
            const log = document.querySelector(".voice-event-list");
            if (log) {
                log.scrollTop = log.scrollHeight;
            }
        });
    }

    function sendEvent(payload, visible = true) {
        if (!socket || socket.readyState !== WebSocket.OPEN) {
            return false;
        }
        socket.send(JSON.stringify(payload));
        if (visible) {
            appendEvent("sent", payload);
        }
        return true;
    }

    function getPlaybackState(responseId) {
        if (!responsePlayback.has(responseId)) {
            responsePlayback.set(responseId, { responseDone: false });
        }
        return responsePlayback.get(responseId);
    }

    function playNextAudio() {
        if (disposed || !audioContext) {
            return;
        }

        while (playbackQueue.length > 0) {
            const next = playbackQueue.shift();
            const binary = atob(next.audio);
            const samples = new Float32Array(binary.length / 2);
            const view = new DataView(new ArrayBuffer(binary.length));
            for (let index = 0; index < binary.length; index++) {
                view.setUint8(index, binary.charCodeAt(index));
            }
            for (let index = 0; index < samples.length; index++) {
                samples[index] = view.getInt16(index * 2, true) / 0x8000;
            }

            const buffer = audioContext.createBuffer(1, samples.length, REALTIME_SAMPLE_RATE);
            buffer.copyToChannel(samples, 0);
            const source = audioContext.createBufferSource();
            source.buffer = buffer;
            source.connect(audioContext.destination);

            const itemKey = `${next.responseId}:${next.itemId}:${next.contentIndex}`;
            const offsetMs = itemPlaybackMs.get(itemKey) || 0;
            const startsAt = Math.max(playbackEndTime, audioContext.currentTime + 0.02);
            const playback = {
                responseId: next.responseId,
                itemId: next.itemId,
                contentIndex: next.contentIndex,
                source,
                startsAt,
                endsAt: startsAt + buffer.duration,
                offsetMs,
                durationMs: buffer.duration * 1000
            };
            itemPlaybackMs.set(itemKey, offsetMs + playback.durationMs);
            playbackEndTime = playback.endsAt;
            scheduledPlaybacks.push(playback);
            setVoiceActivity("assistant");
            source.onended = () => {
                scheduledPlaybacks = scheduledPlaybacks.filter(item => item.source !== source);
                if (scheduledPlaybacks.length === 0) {
                    playbackEndTime = 0;
                }
                playNextAudio();
            };
            source.start(startsAt);
        }

        if (scheduledPlaybacks.length === 0
            && connectionState.value === "connected"
            && voiceActivity.value === "assistant"
            && !activeResponseId) {
            setVoiceActivity(pendingToolCalls.value.some(call => !call.submitted) ? "waiting-tool" : "listening");
        }
    }

    function interruptPlayback(sendTruncation) {
        const now = audioContext?.currentTime || 0;
        const audiblePlayback = scheduledPlaybacks.find(playback => playback.startsAt <= now && now < playback.endsAt);
        const responseId = audiblePlayback?.responseId || scheduledPlaybacks[0]?.responseId || activeResponseId;
        if (sendTruncation && responseId) {
            cancelledResponseIds.add(responseId);
            if (!getPlaybackState(responseId).responseDone) {
                sendEvent({ type: "response.cancel", response_id: responseId });
            }
        }
        if (sendTruncation && audiblePlayback?.itemId) {
            const elapsedMs = Math.min(audiblePlayback.durationMs, (now - audiblePlayback.startsAt) * 1000);
            sendEvent({
                type: "conversation.item.truncate",
                item_id: audiblePlayback.itemId,
                content_index: audiblePlayback.contentIndex,
                audio_end_ms: Math.max(0, Math.floor(audiblePlayback.offsetMs + elapsedMs))
            });
        }
        scheduledPlaybacks.forEach(playback => {
            playback.source.onended = null;
            playback.source.stop();
        });
        playbackQueue = [];
        scheduledPlaybacks = [];
        playbackEndTime = 0;
    }

    function handleServerEvent(message) {
        appendEvent("received", message);
        const eventType = message.type;

        if (eventType === "input_audio_buffer.speech_started") {
            interruptPlayback(true);
            setVoiceActivity("speaking");
        }
        else if (eventType === "input_audio_buffer.speech_stopped") {
            setVoiceActivity("thinking");
        }
        else if (eventType === "response.created" || eventType === "response.in_progress") {
            activeResponseId = message.response?.id || message.response_id || activeResponseId;
            setVoiceActivity("thinking");
        }
        else if (eventType === "response.output_audio.delta") {
            if (cancelledResponseIds.has(message.response_id)) {
                return;
            }
            playbackQueue.push({
                responseId: message.response_id,
                itemId: message.item_id,
                contentIndex: message.content_index,
                audio: message.delta
            });
            playNextAudio();
        }
        else if (eventType === "response.output_audio.done") {
            playNextAudio();
        }
        else if (eventType === "response.function_call_arguments.done") {
            let content = message.arguments;
            try {
                content = JSON.parse(message.arguments);
            }
            catch {
            }
            pendingToolCalls.value = [...pendingToolCalls.value, {
                id: message.call_id,
                responseId: message.response_id,
                name: message.name,
                content,
                submitted: false
            }];
            setVoiceActivity("waiting-tool");
        }
        else if (eventType === "response.done") {
            const responseId = message.response?.id;
            if (responseId) {
                getPlaybackState(responseId).responseDone = true;
                activeResponseId = activeResponseId === responseId ? null : activeResponseId;
                completedToolResponses.add(responseId);
                continueToolResponse(responseId);
            }
            if (scheduledPlaybacks.length === 0 && playbackQueue.length === 0) {
                setVoiceActivity(pendingToolCalls.value.some(call => !call.submitted) ? "waiting-tool" : "listening");
            }
        }
        else if (eventType === "conversation.item.input_audio_transcription.delta" || eventType === "conversation.item.input_audio_transcription.completed" || eventType === "response.output_audio_transcript.delta" || eventType === "response.output_audio_transcript.done" || eventType === "session.created" || eventType === "session.updated") {
            if (eventType === "session.updated" && connectionState.value === "connecting") {
                setVoiceActivity("listening");
            }
        }
        else if (eventType === "error") {
            setVoiceActivity("error");
        }
    }

    async function startMicrophone() {
        microphoneStream = await navigator.mediaDevices.getUserMedia({
            audio: {
                channelCount: 1,
                echoCancellation: true,
                noiseSuppression: true,
                autoGainControl: true
            }
        });
        audioContext = new (window.AudioContext || window.webkitAudioContext)({ sampleRate: REALTIME_SAMPLE_RATE });
        await audioContext.resume();
        microphoneSource = audioContext.createMediaStreamSource(microphoneStream);
        microphoneProcessor = audioContext.createScriptProcessor(4096, 1, 1);
        silentOutput = audioContext.createGain();
        silentOutput.gain.value = 0;

        microphoneProcessor.onaudioprocess = event => {
            if (!socket || socket.readyState !== WebSocket.OPEN) {
                return;
            }

            const sourceSamples = event.inputBuffer.getChannelData(0);
            const sampleRatio = audioContext.sampleRate / REALTIME_SAMPLE_RATE;
            const samples = sampleRatio === 1
                ? sourceSamples
                : Float32Array.from(
                    { length: Math.floor(sourceSamples.length / sampleRatio) },
                    (_, index) => sourceSamples[Math.min(sourceSamples.length - 1, Math.floor(index * sampleRatio))]
                );
            const pcm = new Uint8Array(samples.length * 2);
            const view = new DataView(pcm.buffer);
            let energy = 0;
            for (let index = 0; index < samples.length; index++) {
                const sample = Math.max(-1, Math.min(1, samples[index]));
                energy += sample * sample;
                view.setInt16(index * 2, sample < 0 ? sample * 0x8000 : sample * 0x7fff, true);
            }

            const binaryParts = [];
            for (let offset = 0; offset < pcm.length; offset += 0x8000) {
                binaryParts.push(String.fromCharCode(...pcm.subarray(offset, offset + 0x8000)));
            }
            sendEvent({
                type: "input_audio_buffer.append",
                audio: btoa(binaryParts.join(""))
            }, false);

            const level = Math.min(1, Math.sqrt(energy / samples.length) * 7);
            document.querySelector(".voice-stage")?.style.setProperty("--voice-level", level.toFixed(3));
        };

        microphoneSource.connect(microphoneProcessor);
        microphoneProcessor.connect(silentOutput);
        silentOutput.connect(audioContext.destination);
    }

    async function stopSession() {
        if (connectionState.value === "disconnected" && !socket && !microphoneStream) {
            return;
        }

        connectionState.value = "disconnecting";
        interruptPlayback(false);
        microphoneProcessor?.disconnect();
        microphoneSource?.disconnect();
        silentOutput?.disconnect();
        microphoneStream?.getTracks().forEach(track => track.stop());
        if (audioContext && audioContext.state !== "closed") {
            await audioContext.close();
        }
        microphoneProcessor = null;
        microphoneSource = null;
        silentOutput = null;
        microphoneStream = null;
        audioContext = null;

        const closingSocket = socket;
        socket = null;
        if (closingSocket && closingSocket.readyState < WebSocket.CLOSING) {
            closingSocket.close(1000, "Voice session ended by user");
        }

        pendingToolCalls.value = [];
        completedToolResponses.clear();
        activeResponseId = null;
        responsePlayback.clear();
        itemPlaybackMs.clear();
        cancelledResponseIds.clear();
        playbackQueue = [];
        scheduledPlaybacks = [];
        playbackEndTime = 0;
        connectionState.value = "disconnected";
        setVoiceActivity("idle");
    }

    async function startSession() {
        if (connectionState.value !== "disconnected") {
            return;
        }

        if (!navigator.mediaDevices?.getUserMedia) {
            displayAlert({
                title: "Microphone unavailable",
                message: "This browser cannot capture microphone audio."
            });
            return;
        }

        const tools = toolsControl.value;
        if (!Array.isArray(tools)) {
            displayAlert({
                title: "Check the tools",
                message: "Tools must be a valid JSON array."
            });
            return;
        }

        connectionState.value = "connecting";
        events.value = [];
        disposed = false;

        try {
            const session = getSession();
            const endpoint = new URL(`${window.env.apiEndpoint}/api/v1/voice-session`);
            endpoint.protocol = endpoint.protocol === "https:" ? "wss:" : "ws:";
            endpoint.searchParams.set("api-key", session.sessionKey);
            socket = new WebSocket(endpoint);

            await new Promise((resolve, reject) => {
                socket.onopen = resolve;
                socket.onerror = () => reject(new Error("The voice WebSocket connection could not be opened."));
            });

            socket.onmessage = event => {
                if (event.data === "keep-alive") {
                    return;
                }

                try {
                    handleServerEvent(JSON.parse(event.data));
                }
                catch (error) {
                    appendEvent("client", {
                        type: "invalid_server_event",
                        error: { message: error.message }
                    });
                }
            };
            socket.onclose = event => {
                appendEvent("client", {
                    type: "session.closed",
                    code: event.code,
                    reason: event.reason || null
                });
                if (connectionState.value !== "disconnected") {
                    void stopSession();
                }
            };

            sendEvent({
                type: "session.update",
                gateway: gatewayControl.value,
                session: {
                    type: "realtime",
                    model: modelControl.value,
                    output_modalities: ["audio"],
                    audio: {
                        input: {
                            format: { type: "audio/pcm", rate: REALTIME_SAMPLE_RATE },
                            turn_detection: {
                                type: "server_vad",
                                threshold: 0.5,
                                prefix_padding_ms: 300,
                                silence_duration_ms: 500,
                                create_response: true,
                                interrupt_response: false
                            }
                        },
                        output: {
                            format: { type: "audio/pcm", rate: REALTIME_SAMPLE_RATE },
                            voice: voiceControl.value
                        }
                    },
                    reasoning: { effort: reasoningControl.value },
                    instructions: contextControl.value || null,
                    tools
                }
            });
            await startMicrophone();
            connectionState.value = "connected";
            setVoiceActivity("listening");
        }
        catch (error) {
            await stopSession();
            displayAlert({
                title: "Voice session unavailable",
                message: error.message
            });
        }
    }

    function continueToolResponse(responseId) {
        const calls = pendingToolCalls.value.filter(call => call.responseId === responseId);
        if (!completedToolResponses.has(responseId) || calls.length === 0 || calls.some(call => !call.submitted)) {
            return;
        }

        sendEvent({ type: "response.create" });
        completedToolResponses.delete(responseId);
        pendingToolCalls.value = pendingToolCalls.value.filter(call => call.responseId !== responseId);
        setVoiceActivity("thinking");
    }

    function submitToolResult(toolCall, input) {
        const result = input.value.trim();
        if (!result || !sendEvent({
            type: "conversation.item.create",
            item: {
                type: "function_call_output",
                call_id: toolCall.id,
                output: result
            }
        })) {
            return;
        }
        pendingToolCalls.value = pendingToolCalls.value.map(call => call.id === toolCall.id ? { ...call, submitted: true } : call);
        continueToolResponse(toolCall.responseId);
    }

    const sessionButton = el.state.listening([connectionState], () =>
        connectionState.value === "disconnected"
            ? PrimaryButton(Icon("phone-line"), "Start session", { onClick: startSession })
            : DangerButton(
                Icon("phone-off-line"),
                connectionState.value === "connecting" ? "Connecting..." : "End session",
                {
                    disabled: connectionState.value === "disconnecting",
                    onClick: stopSession
                }
            )
    );

    const stage = el.state.listening([connectionState, voiceActivity], () => {
        const labels = {
            idle: ["Ready when you are", "Start a session to begin speaking."],
            listening: ["Listening", "Speak naturally when you are ready."],
            detecting: ["Voice detected", "Keep speaking—I'm listening."],
            speaking: ["You're speaking", "The current response will pause for you."],
            thinking: ["Thinking", "Preparing a spoken response."],
            assistant: ["Speaking", "You can interrupt at any time."],
            "waiting-tool": ["Tool result needed", "Provide the result below to continue."],
            error: ["Something went wrong", "Check the latest WebSocket event."]
        };
        const [title, subtitle] = labels[voiceActivity.value] || labels.idle;

        return el("section.voice-stage",
            {
                classList: [`voice-activity-${voiceActivity.value}`],
                ariaLive: "polite"
            },
            el("div.voice-orbit",
                el("div.voice-orbit-core", Icon(connectionState.value === "connected" ? "mic-line" : "mic-off-line")),
                el("div.voice-orbit-ring.voice-orbit-ring-one"),
                el("div.voice-orbit-ring.voice-orbit-ring-two")
            ),
            el("div.voice-wave[aria-hidden=true]",
                ...Array.from({ length: 17 }, (_, index) => el("span", { style: { "--bar-index": index } }))
            ),
            el("div.voice-stage-copy",
                el("h2", title),
                Paragraph(subtitle)
            )
        );
    });

    const toolResults = el.state.listening([pendingToolCalls], () =>
        el("section.voice-tool-results",
            pendingToolCalls.value.length > 0 && el("div.voice-tool-results-heading",
                Icon("hammer-line"),
                el("strong", "Client tool calls"),
                MutedText("Provide a result for each invocation.")
            ),
            ...pendingToolCalls.value.map(toolCall => {
                const resultInput = TextArea(null, null, {
                    placeholder: "Enter the tool result...",
                    ariaLabel: `Result for ${toolCall.name}`
                });
                return el("article.voice-tool-call",
                    el("div.voice-tool-call-heading",
                        el("strong", toolCall.name),
                        MutedText(toolCall.id)
                    ),
                    el("pre", JSON.stringify(toolCall.content, null, 2)),
                    FormControl("Result", resultInput),
                    PrimaryButton(Icon("send-plane-2-line"), "Submit result", {
                        onClick() {
                            submitToolResult(toolCall, resultInput);
                        }
                    })
                );
            })
        )
    );

    const eventLog = el.state.listening([events], () =>
        el("div.voice-event-list",
            events.value.length === 0
                ? el("div.voice-event-empty", Icon("pulse-line"), MutedText("WebSocket events will appear here."))
                : events.value.map(event =>
                    el("article.voice-event",
                        el("div.voice-event-heading",
                            el("span.voice-event-direction", {
                                classList: [`voice-event-${event.direction}`]
                            }, event.direction),
                            el("strong", event.payload.type || "event"),
                            el("time", event.time.toLocaleTimeString())
                        ),
                        el("pre", JSON.stringify(event.payload, (key, value) =>
                            key === "data" && typeof value === "string"
                                ? `[${value.length} base64 characters]`
                                : value, 2))
                    )
                )
        )
    );

    const root = Dashboard(
        { classList: ["full-size"] },
        Card(
            CardNavigation(
                CommandBar(
                    sessionButton,
                    el.state.listening([connectionState], () =>
                        el(".app-button.voice-connection-status[readonly]",
                            el("span"),
                            connectionState.value === "connected" ? "Connected" : connectionState.value
                        )
                    )
                )
            ),
            Playground(
                HorizontalSizableContainer(
                    { classList: ["voice-layout"] },
                    HorizontalSizableSection(
                        { initialSize: "400px" },
                        PlaygroundAside(
                            TabControl([
                                {
                                    title: "Session",
                                    icon: Icon("equalizer-2-fill"),
                                    contents: PlaygroundAsideContainer(
                                        SizableContainer(
                                            SizableSection("Parameters",
                                                gatewayControl,
                                                modelControl,
                                                voiceControl,
                                                reasoningControl
                                            ),
                                            SizableSection("Context", contextControl)
                                        )
                                    )
                                },
                                {
                                    title: "Tools",
                                    icon: Icon("hammer-line"),
                                    contents: PlaygroundAsideContainer(
                                        SizableContainer(
                                            SizableSection("Tools", toolsControl)
                                        )
                                    )
                                }
                            ], { type: "vertical" })
                        )
                    ),
                    HorizontalSizableSection(
                        PlaygroundContent(
                            el("div.voice-workspace",
                                el("div.voice-conversation",
                                    stage,
                                    toolResults
                                ),
                                el("aside.voice-events",
                                    el("div.voice-events-heading",
                                        el("div",
                                            Icon("pulse-line"),
                                            el("span", "WebSocket events")
                                        ),
                                        Dropdown(
                                            null,
                                            DropdownItem("Clear events", Icon("eraser-line"), {
                                                onClick() {
                                                    events.value = [];
                                                }
                                            }),
                                            DropdownItem("Copy to clipboard", Icon("file-copy-line"), {
                                                onClick() {
                                                    copyToClipboard(JSON.stringify(events.value, (key, value) =>
                                                        key === "data" && typeof value === "string"
                                                            ? `[${value.length} base64 characters]`
                                                            : value, 2));
                                                }
                                            }),
                                        )
                                    ),
                                    eventLog
                                )
                            )
                        )
                    )
                )
            )
        )
    );

    const observer = new MutationObserver(() => {
        if (!root.isConnected) {
            disposed = true;
            observer.disconnect();
            void stopSession();
        }
    });
    observer.observe(document.body, { childList: true, subtree: true });

    return root;
}
